'use client';

import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  Dropdown,
  Flex,
  Heading,
  Icon,
  Pill,
  Text,
} from '@/_components';
import styles from '../../ReviewDialog.module.css';
import chipStyles from '../PageInputBox/PageInputBox.module.css';
import type { BulkDocument } from '@/_types/models/documents';
import type { DocumentsPanelProps } from './DocumentsPanel.types';
import { convertToRanges } from '@/_utils/documents';
import { rangeParser } from '@/_utils/parse';
import { PageInputBox } from '../PageInputBox/PageInputBox';
import { AddDocumentSection } from '../AddDocumentSection';
import { useBulkUploadProcessor } from '@/_contexts/admin/BulkUploadProcessorProvider';

export const DocumentsPanel = ({
  documents,
  setDocuments,
  currentlySelectedDoc,
  setCurrentlySelectedDoc,
  isLoading,
  isDragging,
  setIsDragging,
  pages,
  showPageChips,
  setShowPageChips,
  setShowDeletedPages,
  showDeletedPages,
  editingDocId,
  setEditingDocId,
  questionnaire,
}: DocumentsPanelProps) => {
  const {
    activePageFilter,
    setActivePageFilter,
    togglePageFilter,
    selectedDocChipPages,
    clearDocChipSelection,
    selectedUnclassifiedPages,
    setSelectedUnclassifiedPages,
    clearUnclassifiedSelection,
  } = useBulkUploadProcessor();
  const UNCLASSIFIED_KEY = '__unclassified__';

  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});
  const [allCollapsed, setAllCollapsed] = useState<boolean>(false);
  const didAutoExpandUnclassified = useRef<boolean>(false);
  const [documentOrder, setDocumentOrder] = useState<number[]>([]);

  // Unclassified selection state
  const unclassifiedListRef = useRef<HTMLDivElement>(null);
  const unclassifiedDragPreviewRef = useRef<HTMLDivElement | null>(null);
  const [ucDragStartY, setUcDragStartY] = useState<number | null>(null);
  const [ucDragStartX, setUcDragStartX] = useState<number | null>(null);
  const [ucSelectionRect, setUcSelectionRect] = useState<{
    top: number;
    height: number;
    left?: number;
    right?: number;
  } | null>(null);

  const unclassifiedPages: Array<number> = useMemo(() => {
    const assigned = new Set<number>();
    (documents || []).forEach((d: any) =>
      (d.pages || []).forEach((p: number) => assigned.add(p)),
    );
    return (pages || [])
      .map((p: any) => p.pageNumber)
      .filter((p: number) => !assigned.has(p));
  }, [documents, pages]);

  useEffect(() => {
    const handleWindowMouseUp = () => {
      if (ucDragStartY != null || ucDragStartX != null) {
        setUcDragStartY(null);
        setUcDragStartX(null);
        setUcSelectionRect(null);
      }
    };
    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, [ucDragStartY, ucDragStartX]);

  // Simple approach: maintain document order unless documents have pages
  const orderedDocuments = [...documents].sort((doc, doc2) => {
    const docHasPages = doc.pages && doc.pages.length > 0;
    const doc2HasPages = doc2.pages && doc2.pages.length > 0;
    const docId = doc.id || 0;
    const doc2Id = doc2.id || 0;

    // If both have pages, sort by page number
    if (docHasPages && doc2HasPages) {
      const [firstPage] = rangeParser(doc.pageRanges || '0');
      const [secondPage] = rangeParser(doc2.pageRanges || '0');
      return firstPage - secondPage;
    }

    // For all other cases, maintain the current document order
    const docIndex = documentOrder.indexOf(docId);
    const doc2Index = documentOrder.indexOf(doc2Id);

    // If both documents are in the order array, use their positions
    if (docIndex !== -1 && doc2Index !== -1) {
      return docIndex - doc2Index;
    }

    // If only one is in the order array, prioritize it
    if (docIndex !== -1 && doc2Index === -1) return -1;
    if (docIndex === -1 && doc2Index !== -1) return 1;

    // If neither is in the order array, maintain current order
    return 0;
  });

  // Update documentOrder whenever the sorted order changes
  useEffect(() => {
    if (orderedDocuments.length > 0) {
      const newOrder = orderedDocuments.map((doc) => doc.id || 0);
      // Only update if the order actually changed
      if (JSON.stringify(newOrder) !== JSON.stringify(documentOrder)) {
        setDocumentOrder(newOrder);
      }
    }
  }, [orderedDocuments, documentOrder]);

  const groups = orderedDocuments.reduce<Record<string, BulkDocument[]>>(
    (acc, d) => {
      const key = `${d.name} (${d.differentiator})`;
      acc[key] = acc[key] || [];
      acc[key].push(d);
      return acc;
    },
    {},
  );

  // Initialize document order on first load
  useEffect(() => {
    if (documents.length > 0 && documentOrder.length === 0) {
      setDocumentOrder(documents.map((doc) => doc.id || 0));
    }
  }, [documents, documentOrder.length]);

  // Default: collapse all document sections (and Unclassified). Do this only on first load
  useEffect(() => {
    if (Object.keys(collapsedGroups).length === 0) {
      const initial: Record<string, boolean> = {};
      Object.keys(groups).forEach((k) => (initial[k] = true));
      initial[UNCLASSIFIED_KEY] = true;
      setCollapsedGroups(initial);
      setAllCollapsed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  useEffect(() => {
    if (unclassifiedPages.length === 0 && !collapsedGroups[UNCLASSIFIED_KEY]) {
      setCollapsedGroups((prev) => ({ ...prev, [UNCLASSIFIED_KEY]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unclassifiedPages]);

  // After data loads, if Unclassified has pages and is collapsed from init, expand it once
  useEffect(() => {
    if (
      !didAutoExpandUnclassified.current &&
      unclassifiedPages.length > 0 &&
      collapsedGroups[UNCLASSIFIED_KEY]
    ) {
      didAutoExpandUnclassified.current = true;
      setCollapsedGroups((prev) => ({ ...prev, [UNCLASSIFIED_KEY]: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unclassifiedPages]);

  const setAllGroups = useCallback(
    (collapsed: boolean) => {
      const newState: Record<string, boolean> = {};
      Object.keys(groups).forEach((k) => {
        newState[k] = collapsed;
      });
      newState[UNCLASSIFIED_KEY] = collapsed;
      setCollapsedGroups(newState);
      setAllCollapsed(collapsed);
    },
    [groups],
  );

  const handleDocDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      // Always allow drop; set a helpful dropEffect when known types are present
      e.preventDefault();
      const hasKnownType =
        e.dataTransfer.types.includes('application/x-page-number') ||
        e.dataTransfer.types.includes('application/x-page-numbers') ||
        e.dataTransfer.types.includes('text/plain');
      e.dataTransfer.dropEffect = hasKnownType ? 'move' : 'none';
    },
    [],
  );

  const addPagesToDocument = (
    targetDoc: BulkDocument | undefined,
    pageNumbers: number[],
  ) => {
    if (!targetDoc || !pageNumbers || pageNumbers.length === 0) return;
    const uniqueToMove = Array.from(new Set(pageNumbers));
    const toMoveSet = new Set(uniqueToMove);
    const newDocuments = documents.map((d) => {
      const existing = Array.isArray(d.pages) ? d.pages : [];
      if (d === (targetDoc as any) || d.id === targetDoc.id) {
        const merged = Array.from(new Set([...existing, ...uniqueToMove])).sort(
          (a, b) => a - b,
        );
        return {
          ...d,
          pages: merged,
          pageRanges: convertToRanges(merged).join(', '),
        };
      }
      // Remove moved pages from all other documents
      const filtered = existing.filter((p: number) => !toMoveSet.has(p));
      if (filtered.length !== existing.length) {
        return {
          ...d,
          pages: filtered,
          pageRanges: convertToRanges(filtered).join(', '),
        };
      }
      return d;
    });
    setDocuments(newDocuments);
    // Select the destination document and clear any page selections
    const selected = newDocuments.find((d) => d.id === targetDoc.id) as any;
    if (selected) setCurrentlySelectedDoc(selected);
    clearUnclassifiedSelection();
    clearDocChipSelection();
  };

  const handleDocDrop = (
    e: React.DragEvent<HTMLDivElement>,
    doc?: BulkDocument,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const multi = e.dataTransfer.getData('application/x-page-numbers');
    if (multi) {
      try {
        const pages: number[] = JSON.parse(multi);
        addPagesToDocument(doc, pages);
        return;
      } catch {}
    } else {
      const text = e.dataTransfer.getData('text/plain');
      if (text) {
        try {
          const parsed = JSON.parse(text);
          if (Array.isArray(parsed?.pages)) {
            addPagesToDocument(doc, parsed.pages as number[]);
            return;
          }
        } catch {}
      }
    }
    const data = e.dataTransfer.getData('application/x-page-number');
    const pageNumber = Number(data);
    if (!Number.isNaN(pageNumber)) addPagesToDocument(doc, [pageNumber]);
  };

  const formatUcDragLabel = (pagesToMove: number[]): string => {
    const sorted = [...pagesToMove].sort((a, b) => a - b);
    if (sorted.length === 1) return `Page ${sorted[0]}`;
    if (sorted.length === 2) return `Pages ${sorted[0]} and ${sorted[1]}`;
    return `Pages ${sorted.slice(0, -1).join(', ')} and ${sorted[sorted.length - 1]}`;
  };

  const setUcDragPreview = useCallback(
    (event: React.DragEvent, pagesToMove: number[]) => {
      try {
        let previewEl: HTMLDivElement;
        if (pagesToMove.length === 1) {
          const source = event.currentTarget as HTMLElement;
          previewEl = source.cloneNode(true) as HTMLDivElement;
          previewEl.style.pointerEvents = 'none';
          previewEl.style.position = 'fixed';
          previewEl.style.left = '-1000px';
          previewEl.style.top = '-1000px';
          previewEl.style.transform = 'scale(0.95)';
          previewEl.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)';
        } else {
          previewEl = document.createElement('div');
          previewEl.textContent = formatUcDragLabel(pagesToMove);
          previewEl.style.pointerEvents = 'none';
          previewEl.style.display = 'inline-flex';
          previewEl.style.alignItems = 'center';
          previewEl.style.padding = '4px 8px';
          previewEl.style.background = 'var(--accent-600)';
          const source = event.currentTarget as HTMLElement;
          let bg = '';
          let fg = '';
          const tryEls = [
            source,
            ...(Array.from(
              source.querySelectorAll('*'),
            ) as unknown as HTMLElement[]),
          ];
          for (const el of tryEls) {
            const cs = getComputedStyle(el);
            const candidateBg = cs.backgroundColor;
            if (
              candidateBg &&
              candidateBg !== 'transparent' &&
              !candidateBg.endsWith(', 0)')
            ) {
              bg = candidateBg;
              fg = cs.color;
              break;
            }
          }
          previewEl.style.background = bg || 'var(--accent)';
          previewEl.style.color = fg || 'white';
          previewEl.style.borderRadius = '999px';
          previewEl.style.fontSize = '11px';
          previewEl.style.fontWeight = '700';
          previewEl.style.whiteSpace = 'nowrap';
          previewEl.style.maxWidth = '180px';
          previewEl.style.transform = 'scale(0.9)';
          previewEl.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)';
        }
        document.body.appendChild(previewEl);
        unclassifiedDragPreviewRef.current = previewEl;
        event.dataTransfer.setDragImage(previewEl, 12, 12);
      } catch {}
    },
    [],
  );

  const scrollToPage = (pageNumber: number) => {
    const el = document.querySelector(
      `img[alt="Page ${pageNumber}"]`,
    ) as HTMLElement | null;
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Header actions
  const handleToggleAll = useCallback(
    () => setAllGroups(!allCollapsed),
    [allCollapsed, setAllGroups],
  );
  const handleToggleGroup = useCallback(
    (key: string) =>
      setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] })),
    [],
  );
  const handleUnclassifiedHeaderClick = useCallback(() => {
    if (unclassifiedPages.length !== 0) {
      handleToggleGroup(UNCLASSIFIED_KEY);
    }
  }, [handleToggleGroup, unclassifiedPages, UNCLASSIFIED_KEY]);

  // Unclassified marquee handlers
  const handleUcMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement | null;
      if (target && target.closest('[data-chip-pill]')) return;
      // If marquee starts in Unclassified, deselect any currently selected document
      if (currentlySelectedDoc) {
        setCurrentlySelectedDoc(null);
        clearDocChipSelection();
      }
      const rect = unclassifiedListRef.current?.getBoundingClientRect();
      if (!rect) return;
      // Clamp to bounds to prevent jumpy marquee origins
      const startY = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
      const startX = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      setUcDragStartY(startY);
      setUcDragStartX(startX);
      setUcSelectionRect({
        top: startY,
        height: 0,
        left: startX,
        right: startX,
      });
      setSelectedUnclassifiedPages([]);
    },
    [
      currentlySelectedDoc,
      setCurrentlySelectedDoc,
      clearDocChipSelection,
      setSelectedUnclassifiedPages,
    ],
  );

  const updateUcSelectionFromClient = useCallback(
    (clientX: number, clientY: number) => {
      if (ucDragStartY == null || !unclassifiedListRef.current) return;
      const rect = unclassifiedListRef.current.getBoundingClientRect();
      const currentY = clientY - rect.top;
      const top = Math.min(ucDragStartY, currentY);
      const height = Math.abs(currentY - ucDragStartY);
      const currentX = clientX - rect.left;
      const startLeft = ucDragStartX ?? currentX;
      const startRight = ucDragStartX ?? currentX;
      const left = Math.min(startLeft, currentX);
      const right = Math.max(startRight, currentX);
      setUcSelectionRect({ top, height, left, right });
      const rows = Array.from(
        unclassifiedListRef.current.querySelectorAll('[data-chip-row]'),
      ) as HTMLDivElement[];
      const selected: number[] = [];
      rows.forEach((row) => {
        const pill = row.querySelector(
          '[data-chip-pill]',
        ) as HTMLElement | null;
        const target = pill ?? row;
        const r = target.getBoundingClientRect();
        const x = r.left - rect.left;
        const y = r.top - rect.top;
        const w = r.width;
        const h = r.height;
        const intersects = !(
          x > (right ?? x) ||
          x + w < (left ?? x) ||
          y > top + height ||
          y + h < top
        );
        if (intersects) {
          const pg = Number(row.dataset.page);
          if (!Number.isNaN(pg)) selected.push(pg);
        }
      });
      setSelectedUnclassifiedPages(selected);
    },
    [ucDragStartX, ucDragStartY, setSelectedUnclassifiedPages],
  );

  const handleUcMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!unclassifiedListRef.current) return;
      const clientX = e.clientX;
      const clientY = e.clientY;
      updateUcSelectionFromClient(clientX, clientY);
    },
    [updateUcSelectionFromClient],
  );

  const handleUcMouseUp = useCallback(() => {
    setUcDragStartY(null);
    setUcDragStartX(null);
    setUcSelectionRect(null);
  }, []);

  // Allow marquee to continue when cursor leaves the Unclassified box
  useEffect(() => {
    if (ucDragStartY == null) return;
    const onMove = (e: MouseEvent) =>
      updateUcSelectionFromClient(e.clientX, e.clientY);
    const onUp = () => {
      setUcDragStartY(null);
      setUcDragStartX(null);
      setUcSelectionRect(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [ucDragStartY, updateUcSelectionFromClient]);

  // Unclassified drag handlers
  const handleUcDragStart = useCallback(
    (page: number) => (e: React.DragEvent) => {
      const moving =
        selectedUnclassifiedPages.length > 0 &&
        selectedUnclassifiedPages.includes(page)
          ? selectedUnclassifiedPages
          : [page];
      if (moving.length > 1) {
        e.dataTransfer.setData(
          'application/x-page-numbers',
          JSON.stringify(moving),
        );
      } else {
        e.dataTransfer.setData('application/x-page-number', String(moving[0]));
      }
      e.dataTransfer.setData('text/plain', JSON.stringify({ pages: moving }));
      e.dataTransfer.effectAllowed = 'move';
      setUcDragPreview(e, moving);
      setIsDragging(true);
    },
    [selectedUnclassifiedPages, setIsDragging, setUcDragPreview],
  );

  const handleUcDragEnd = useCallback(() => {
    setIsDragging(false);
    if (unclassifiedDragPreviewRef.current) {
      document.body.removeChild(unclassifiedDragPreviewRef.current);
      unclassifiedDragPreviewRef.current = null;
    }
  }, [setIsDragging]);

  const handleUnclassifiedDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const removePages = (pagesToRemove: number[]) => {
        const newDocuments = documents.map((d) => {
          const pgs = Array.isArray(d.pages) ? d.pages : [];
          const filtered = pgs.filter(
            (p: number) => !pagesToRemove.includes(p),
          );
          return pgs.length !== filtered.length
            ? {
                ...d,
                pages: filtered,
                pageRanges: convertToRanges(filtered).join(', '),
              }
            : d;
        });
        setDocuments(newDocuments);
      };
      const multi = e.dataTransfer.getData('application/x-page-numbers');
      if (multi) {
        try {
          const pagesParsed: number[] = JSON.parse(multi);
          removePages(pagesParsed);
          return;
        } catch {}
      }
      const single = e.dataTransfer.getData('application/x-page-number');
      const pageNumber = Number(single);
      if (!Number.isNaN(pageNumber)) removePages([pageNumber]);
    },
    [setDocuments, documents],
  );

  return (
    <Flex className={styles.flexInPanelRight} isColumn={true} gap={8}>
      {/* Fixed Header Section */}
      <Flex gap={4} isColumn={true}>
        <Flex className={styles.groupHeader} isBetween isYCentered>
          <Heading level={5}>Documents ({documents.length})</Heading>
          <Flex isYCentered gap={4}>
            <Dropdown
              items={[
                {
                  name: showDeletedPages ? 'Hide Deleted' : 'Show Deleted',
                  onClick: () => setShowDeletedPages(!showDeletedPages),
                },
                {
                  name: showPageChips
                    ? 'Switch to Range View'
                    : 'Switch to Page View',
                  onClick: () => setShowPageChips(!showPageChips),
                },
              ]}
            >
              <Button intent="ghost" size="tiny" value="Options" />
            </Dropdown>
            <ButtonGroup>
              <Button
                intent="ghost"
                size="tiny"
                value={allCollapsed ? 'Expand all' : 'Collapse all'}
                onClick={handleToggleAll}
              />
            </ButtonGroup>
          </Flex>
        </Flex>
        <Alert type="info" icon="info">
          {showPageChips
            ? 'Click to view or select. Drag page to move or drag on empty space to select multiple pages; then drag to re-associate or create a new document.'
            : 'Review the page range for each document. Drag unclassified pages to add them to a document.'}
        </Alert>

        <AddDocumentSection
          isLoading={isLoading}
          setDocuments={setDocuments}
          selectedPages={
            Object.values(selectedDocChipPages).flat().length > 0
              ? Object.values(selectedDocChipPages).flat()
              : selectedUnclassifiedPages
          }
          onClearSelection={() => {
            if (Object.values(selectedDocChipPages).flat().length > 0) {
              clearDocChipSelection();
            } else {
              clearUnclassifiedSelection();
            }
          }}
          questionnaire={questionnaire}
        />

        {/* Unclassified Section - Now in Fixed Header */}
        <Flex
          isColumn
          className={`${styles.droppableZone}`}
          onDragOver={(e) => handleDocDragOver(e)}
          onDragEnter={(e) => {
            handleDocDragOver(e);
            const t = e.currentTarget as HTMLDivElement;
            const depth = Number(t.dataset.dragDepth || '0') + 1;
            t.dataset.dragDepth = String(depth);
            t.classList.add(styles.droppableZoneActive);
          }}
          onDragLeave={(e) => {
            const t = e.currentTarget as HTMLDivElement;
            const depth = Math.max(0, Number(t.dataset.dragDepth || '1') - 1);
            t.dataset.dragDepth = String(depth);
            if (depth === 0) t.classList.remove(styles.droppableZoneActive);
          }}
          onDrop={(e) => {
            const t = e.currentTarget as HTMLDivElement;
            t.classList.remove(styles.droppableZoneActive);
            delete (t as any).dataset.dragDepth;
            handleUnclassifiedDrop(e);
          }}
        >
          <Flex
            isYCentered={true}
            gap={6}
            className={styles.groupHeaderClickable}
            onClick={handleUnclassifiedHeaderClick}
          >
            <Icon
              name={
                collapsedGroups[UNCLASSIFIED_KEY] ? 'caret-right' : 'caret-down'
              }
              size={16}
              color="secondary"
            />
            <Flex
              yAlign="start"
              xAlign="start"
              gap={4}
              isColumn
              isGrowing={true}
            >
              <Flex isYCentered gap={4}>
                <Heading level={6}>Unclassified</Heading>
                <Pill
                  intent="secondary"
                  label={unclassifiedPages.length.toString()}
                  isRound={true}
                />
              </Flex>
            </Flex>
            <Button
              intent="ghost"
              size="tiny"
              isIconOnly={true}
              iconLeft={
                activePageFilter === UNCLASSIFIED_KEY ? 'eye-filled' : 'eye'
              }
              value="Show only"
              withTooltip
              tooltip="Show only unclassified pages"
              onClick={(e: any) => {
                e.stopPropagation();
                togglePageFilter(UNCLASSIFIED_KEY);
              }}
            />
          </Flex>
          {!collapsedGroups[UNCLASSIFIED_KEY] && (
            <div
              onMouseDown={() => {
                // Clear document chip selection when starting selection here
                clearDocChipSelection();
              }}
            >
              <div
                ref={unclassifiedListRef}
                className={`${chipStyles.chipList} ${chipStyles.chipListRoomy}`}
                onMouseDown={handleUcMouseDown}
                onMouseMove={handleUcMouseMove}
                onMouseUp={handleUcMouseUp}
              >
                {ucSelectionRect &&
                  ucDragStartY != null &&
                  ucDragStartX != null && (
                    <div
                      className={chipStyles.chipSelectionOverlay}
                      style={{
                        top: ucSelectionRect.top,
                        left: Math.min(
                          ucSelectionRect.left ?? 0,
                          ucSelectionRect.right ?? 0,
                        ),
                        width:
                          Math.abs(
                            (ucSelectionRect.right ?? 0) -
                              (ucSelectionRect.left ?? 0),
                          ) || '100%',
                        height: ucSelectionRect.height,
                      }}
                    />
                  )}
                {unclassifiedPages.map((p) => (
                  <div
                    key={p}
                    data-chip-row
                    data-page={p}
                    className={`${chipStyles.chipRow} ${chipStyles.chipDraggable}`}
                    onClick={() => {
                      // If a document is currently selected, deselect it when the user interacts with Unclassified
                      if (currentlySelectedDoc) setCurrentlySelectedDoc(null);
                      scrollToPage(p);
                    }}
                  >
                    <span
                      className={`${chipStyles.chipPillWrap} ${selectedUnclassifiedPages.includes(p) ? chipStyles.chipRowSelected : ''}`}
                      draggable
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onDragStart={handleUcDragStart(p)}
                      onDragEnd={handleUcDragEnd}
                      data-chip-pill
                    >
                      <Pill
                        intent="warning"
                        label={`Page ${p}`}
                        iconLeft="grab"
                      />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Flex>
      </Flex>

      {/* Scrollable Documents Section */}
      <div className={styles.scrollableDocumentsSection}>
        {/* Flat document list with per-document collapse */}
        <Flex isColumn>
          {orderedDocuments
            .filter((doc) => doc.name !== '__to_remove__')
            .map((doc, key) => {
              const groupKey = `${doc.name}${doc.differentiator ? ` (${doc.differentiator})` : ''}`;
              const isCollapsed = !!collapsedGroups[groupKey];
              const hasNoPages = !doc.pages || doc.pages.length === 0;

              return (
                <Flex
                  key={doc.id ?? key}
                  className={`${styles.droppableZone} ${hasNoPages ? styles.documentWithoutPages : ''}`}
                  isColumn
                  data-doc-id={doc.id}
                  onDragOver={(e) => {
                    handleDocDragOver(e);
                  }}
                  onDragEnter={(e) => {
                    handleDocDragOver(e);
                    const t = e.currentTarget as HTMLDivElement;
                    const depth = Number(t.dataset.dragDepth || '0') + 1;
                    t.dataset.dragDepth = String(depth);
                    t.classList.add(styles.droppableZoneActive);
                  }}
                  onDragLeave={(e) => {
                    const t = e.currentTarget as HTMLDivElement;
                    const depth = Math.max(
                      0,
                      Number(t.dataset.dragDepth || '1') - 1,
                    );
                    t.dataset.dragDepth = String(depth);
                    if (depth === 0) {
                      t.classList.remove(styles.droppableZoneActive);
                    }
                  }}
                  onDrop={(e) => {
                    const t = e.currentTarget as HTMLDivElement;
                    t.classList.remove(styles.droppableZoneActive);
                    delete (t as any).dataset.dragDepth;
                    handleDocDrop(e, doc);
                  }}
                >
                  <Flex
                    isYCentered={true}
                    gap={6}
                    className={styles.groupHeaderClickable}
                    onClick={() => {
                      const nextCollapsed = !isCollapsed;
                      setCollapsedGroups((prev) => ({
                        ...prev,
                        [groupKey]: nextCollapsed,
                      }));
                      if (nextCollapsed) {
                        // Collapsing: unselect if this doc is currently selected
                        if (currentlySelectedDoc?.id === doc.id) {
                          setCurrentlySelectedDoc(null);
                          clearDocChipSelection();
                        }
                        // Also clear edit mode and any active filter for this doc
                        if (editingDocId === doc.id) {
                          setEditingDocId(null);
                        }
                        if (activePageFilter === doc.id) {
                          setActivePageFilter(null);
                        }
                      } else {
                        // Expanding: select and scroll to first page
                        setCurrentlySelectedDoc(doc);
                        const [firstPage] = rangeParser(doc.pageRanges || '0');
                        if (firstPage) scrollToPage(firstPage);
                      }
                    }}
                  >
                    <Icon
                      name={isCollapsed ? 'caret-right' : 'caret-down'}
                      size={16}
                      color="secondary"
                    />
                    <Flex
                      yAlign="start"
                      xAlign="start"
                      gap={4}
                      isColumn
                      isGrowing={true}
                    >
                      <Heading level={6}>{groupKey}</Heading>
                      <Text type="tiny" fontWeight="medium" color="secondary">
                        Pages {doc.pageRanges}
                      </Text>
                    </Flex>
                    <Button
                      intent="ghost"
                      size="tiny"
                      isIconOnly={true}
                      iconLeft={
                        editingDocId === doc.id
                          ? 'pencil-simple-filled'
                          : 'pencil-simple'
                      }
                      value="Select pages"
                      withTooltip
                      tooltip="Toggle page selection"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        const next = editingDocId === doc.id ? null : doc.id;
                        // If viewing another doc, clear the view-only filter
                        if (activePageFilter && activePageFilter !== doc.id) {
                          setActivePageFilter(null);
                        }
                        setEditingDocId(next ?? null);
                        if (next) {
                          setCurrentlySelectedDoc(doc);
                          // Ensure section expanded when enabling edit
                          if (isCollapsed) {
                            setCollapsedGroups((prev) => ({
                              ...prev,
                              [groupKey]: false,
                            }));
                          }
                        }
                        if (next) {
                          const [firstPage] = rangeParser(
                            doc.pageRanges || '0',
                          );
                          if (firstPage) scrollToPage(firstPage);
                        }
                      }}
                    />
                    <Button
                      intent="ghost"
                      size="tiny"
                      isIconOnly={true}
                      iconLeft={
                        activePageFilter === doc.id ? 'eye-filled' : 'eye'
                      }
                      value="Show only"
                      withTooltip
                      tooltip="Show only this document's pages"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        // Select this document when toggling filter
                        setCurrentlySelectedDoc(doc);
                        const [firstPage] = rangeParser(doc.pageRanges || '0');
                        if (firstPage) scrollToPage(firstPage);
                        // Ensure section expanded when toggling filter
                        if (isCollapsed) {
                          setCollapsedGroups((prev) => ({
                            ...prev,
                            [groupKey]: false,
                          }));
                        }
                        // Deselect edit mode if another document is currently in edit
                        if (editingDocId && editingDocId !== doc.id) {
                          setEditingDocId(null);
                        }
                        if (doc.id) togglePageFilter(doc.id);
                      }}
                    />
                  </Flex>
                  {!isCollapsed && (
                    <div>
                      <PageInputBox
                        currentlySelectedDoc={currentlySelectedDoc}
                        isDragging={isDragging}
                        setIsDragging={setIsDragging}
                        doc={doc}
                        documents={documents}
                        isLoading={isLoading}
                        showPageChips={showPageChips}
                        setCurrentlySelectedDoc={setCurrentlySelectedDoc}
                        setDocuments={setDocuments}
                        editingDocId={editingDocId}
                      />
                    </div>
                  )}
                </Flex>
              );
            })}
        </Flex>
      </div>
    </Flex>
  );
};
